<template>
  <div class="wrapper" :class="isRTLView ? 'is-rtl' : 'is-ltr'">
    <div ref="container" class="inbox-permissions-container">
      <table class="woot-table" :class="{ 'pointer-events-none': readonly }">
        <thead>
          <tr class="relative">
            <th
              class="sticky-col-header"
              :class="{ 'virtual-box-shadow': scrollLeft }"
            >
              <div class="w-[250px]">
                {{ $t('ROLES.TABLE.HEADER.FEATURE') }}
              </div>
            </th>
            <th
              v-for="inbox in sortedInboxes"
              :key="inbox.id"
              class="text-center min-w-32"
            >
              <div class="w-[120px] break-words">
                {{ inbox.name }}
              </div>
            </th>
            <th class="text-center">
              {{ $t('ROLES.TABLE.HEADER.FUTURE_INBOX') }}
            </th>
            <td class="sticky-border bg-gray-200 !p-0">
              <div class="!p-0"></div>
            </td>
          </tr>
          <tr class="select-all-row relative">
            <td
              class="sticky-col"
              :class="{ 'virtual-box-shadow': scrollLeft }"
            >
              <div>
                <div
                  class="text-gray-600 text-sm mb-1 font-normal rtl:text-right"
                >
                  {{ $t('ROLES.SELECT_ALL') }}
                </div>
              </div>
            </td>
            <td
              v-for="inbox in sortedInboxes"
              :key="`${inbox.id}-select-all`"
              class="!p-0 align-middle"
            >
              <label>
                <input
                  type="checkbox"
                  class="semantic-checkbox !mr-0"
                  :checked="isSelectedAll(inbox.id)"
                  @input="selectAllPermissionsBy(inbox.id, $event)"
                />
              </label>
            </td>
            <td class="!p-0 align-middle">
              <label>
                <input
                  type="checkbox"
                  class="semantic-checkbox !mr-0"
                  :checked="isSelectedAll(0)"
                  @input="selectAllPermissionsBy(0, $event)"
                />
              </label>
            </td>
            <td class="sticky-border !p-0">
              <div class="!p-0"></div>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="inboxPermission in editableInboxPermissions"
            :key="inboxPermission.id"
          >
            <td
              class="sticky-col ltr:pr-[18px] rtl:pl-[18px]"
              :class="{ 'virtual-box-shadow': scrollLeft }"
            >
              <div>
                <div class="text-gray-800 text-sm mb-1">
                  {{ inboxPermissionName(inboxPermission.name) }}
                </div>
                <div class="text-gray-600 text-xs/[18px]">
                  {{ inboxPermissionDesc(inboxPermission.name) }}
                </div>
              </div>
            </td>
            <td
              v-for="inbox in sortedInboxes"
              :key="inbox.id"
              class="!p-0 align-middle"
            >
              <label>
                <input
                  v-model="inboxPermission.ids[inbox.id]"
                  type="checkbox"
                  class="semantic-checkbox !mr-0"
                  @input="checkInboxPermissions(inboxPermission, inbox.id)"
                />
              </label>
            </td>
            <td class="!p-0 align-middle">
              <label>
                <input
                  v-model="inboxPermission.default"
                  type="checkbox"
                  class="semantic-checkbox !mr-0"
                  @input="checkInboxPermissions(inboxPermission)"
                />
              </label>
            </td>
            <td class="sticky-border !p-0">
              <div class="!p-0"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import rtlMixin from 'shared/mixins/rtlMixin';
import { INBOX_PERMISSIONS } from './constants';
export default {
  mixins: [rtlMixin],
  props: {
    value: {
      type: Array,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editableInboxPermissions: this.value,
      scrollLeft: 0,
    };
  },
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
      uiFlags: 'roles/getUIFlags',
    }),
    sortedInboxes() {
      return this.inboxes.slice().sort((a, b) => a.name.localeCompare(b.name));
    },
    selectAllOptions() {
      return this.editableInboxPermissions.reduce((result, permission) => {
        Object.entries(permission.ids).forEach(([key, value]) => {
          (result[key] ??= []).push(value);
        });
        return result;
      }, {});
    },
  },
  watch: {
    value() {
      this.editableInboxPermissions = this.value;
    },
  },
  mounted() {
    this.outsideScrollEl = this.$el.closest('.outside-scroll');
    this.outsideScrollEl?.addEventListener('scroll', this.onScroll);
  },
  beforeDestroy() {
    this.outsideScrollEl?.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll(event) {
      this.scrollLeft = event.target.scrollLeft;
    },
    isSelectedAll(inboxId) {
      if (inboxId === 0) {
        return this.editableInboxPermissions.every(p => p.default === true);
      }
      return this.selectAllOptions[inboxId]?.every(i => i === true);
    },
    selectAllPermissionsBy(inboxId, event) {
      const checked = event.target.checked;
      this.editableInboxPermissions.forEach(permission => {
        if (inboxId === 0) {
          permission.default = checked;
        } else {
          this.$set(permission.ids, inboxId, checked);
        }
      });
    },
    checkInboxPermissions(permission, inboxId = null) {
      setTimeout(() => {
        const val = inboxId ? permission.ids[inboxId] : permission.default;
        const inboxReadGroup = INBOX_PERMISSIONS.slice(0, 4);
        if (inboxReadGroup.includes(permission.name)) {
          this.setInboxReadGroup(permission, inboxId, val);
        }
        if (val) {
          this.setReadInboxPermission(inboxId);
          this.checkReplyInboxPermission(inboxId);
        } else {
          if (permission.name === INBOX_PERMISSIONS[0]) {
            this.unSetInboxPermissions(inboxId, 'all');
          }
          if (permission.name === 'Inbox reply') {
            this.unSetInboxPermissions(inboxId, 'reply');
          }
        }
      }, 100);
    },
    setReadInboxPermission(inboxId) {
      const readInboxPerm = this.editableInboxPermissions.find(
        perm => perm.name === INBOX_PERMISSIONS[0]
      );
      if (inboxId) {
        this.$set(readInboxPerm.ids, inboxId, true);
      } else {
        readInboxPerm.default = true;
      }
    },
    setInboxReadGroup(permission, inboxId = null, value = null) {
      const inboxReadGroup = INBOX_PERMISSIONS.slice(0, 3);
      const isMaster = permission.name === INBOX_PERMISSIONS[3];
      const isFirst = permission.name === INBOX_PERMISSIONS[0];
      // Check master → check all first three
      if (isMaster && value) {
        inboxReadGroup.forEach(pName => {
          const readInboxPerm = this.editableInboxPermissions.find(
            p => p.name === pName
          );
          if (readInboxPerm) {
            if (inboxId) this.$set(readInboxPerm.ids, inboxId, true);
            else readInboxPerm.default = true;
          }
        });
      }

      // Uncheck first → uncheck all 4
      if (isFirst && !value) {
        [...inboxReadGroup, INBOX_PERMISSIONS[3]].forEach(pName => {
          const readInboxPerm = this.editableInboxPermissions.find(
            p => p.name === pName
          );
          if (readInboxPerm) {
            if (inboxId) this.$set(readInboxPerm.ids, inboxId, false);
            else readInboxPerm.default = false;
          }
        });
      }
      // Uncheck any of [0,1,2] → uncheck master
      if (
        !isMaster &&
        !isFirst &&
        inboxReadGroup.includes(permission.name) &&
        !value
      ) {
        const masterPerm = this.editableInboxPermissions.find(
          p => p.name === INBOX_PERMISSIONS[3]
        );
        if (masterPerm) {
          if (inboxId) this.$set(masterPerm.ids, inboxId, false);
          else masterPerm.default = false;
        }
      }
    },
    checkReplyInboxPermission(inboxId) {
      const relatedNames = [
        'Inbox private notes',
        'Inbox attach files',
        'Inbox delay message',
      ];
      const anyRelatedPerm = this.editableInboxPermissions.some(
        perm =>
          relatedNames.includes(perm.name) &&
          (inboxId ? perm.ids[inboxId] : perm.default)
      );
      if (anyRelatedPerm) {
        const replyPerm = this.editableInboxPermissions.find(
          perm => perm.name === 'Inbox reply'
        );
        if (inboxId) {
          this.$set(replyPerm.ids, inboxId, true);
        } else {
          replyPerm.default = true;
        }
      }
    },
    unSetInboxPermissions(inboxId, type) {
      const replyRelatedNames = [
        'Inbox private notes',
        'Inbox attach files',
        'Inbox delay message',
      ];
      this.editableInboxPermissions.forEach(perm => {
        if (type !== 'reply' || replyRelatedNames.includes(perm.name)) {
          if (inboxId) {
            this.$set(perm.ids, inboxId, false);
          } else {
            perm.default = false;
          }
        }
      });
    },
    inboxPermissionName(resource) {
      return this.$t(
        `ROLES.INBOX_PERMISSIONS.${resource.replace(/ /g, '_').toUpperCase()}`
      );
    },
    inboxPermissionTooltip(resource) {
      return this.$t(
        `ROLES.INBOX_PERMISSIONS.${resource
          .replace(/ /g, '_')
          .toUpperCase()}_TOOLTIP`
      );
    },
    inboxPermissionDesc(resource) {
      return this.$t(
        `ROLES.INBOX_PERMISSIONS.${resource
          .replace(/ /g, '_')
          .toUpperCase()}_DESC`
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.wrapper {
  position: relative;
  width: 100%;
  &.inside-scroll::before {
    content: '';
    background: #e5e5e5;
    width: 100px;
    height: 96px;
    right: 1px;
    top: 1px;
    display: block;
    position: absolute;
    border-top-right-radius: 7px;
  }
  &.inside-scroll::after {
    content: '';
    background: #f3f4f6;
    width: 100px;
    height: 40px;
    right: 1px;
    top: 97px;
    display: block;
    position: absolute;
  }

  &.is-rtl {
    &.inside-scroll::before,
    &.inside-scroll::after {
      left: 1px;
      right: unset;
    }
    &.inside-scroll::before {
      border-top-left-radius: 7px;
      border-top-right-radius: 0px;
    }
  }

  &.inside-scroll {
    .inbox-permissions-container {
      @apply h-[calc(100vh-460px)] overflow-auto;
    }
  }

  .virtual-box-shadow {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 274px;
      box-shadow: 4px 0 4px 0 rgba(163, 163, 163, 0.1019607843);
      pointer-events: none;
      height: 100%;
      z-index: 20;
    }
  }

  &.is-rtl {
    .virtual-box-shadow {
      &::before {
        left: unset;
        right: 1px;
      }
      &::after {
        left: unset;
        right: 0;
        box-shadow: -4px 0 4px 0 rgba(163, 163, 163, 0.1019607843);
      }
    }
  }
}

.inbox-permissions-container {
  @apply w-full rounded-[8px];
  overflow-y: clip;

  &::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-track:horizontal {
    margin-left: 274px;
  }

  &::-webkit-scrollbar-track:vertical {
    margin-top: 136px;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-[#c1c1c1] rounded-full;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }

  .woot-table {
    margin-bottom: 0px;
    border-collapse: separate;
    thead {
      @apply bg-neutral-200 rounded-t-[8px] border-none;
      position: sticky;
      top: 0;
      z-index: 5;
      th {
        @apply text-[#717171] text-sm uppercase border-0 px-3 py-2;

        &.sticky-col-header {
          width: 250px;
          position: sticky;
          z-index: 4;
          background-color: #e5e5e5;
          letter-spacing: normal;
        }
      }

      tr.select-all-row {
        td {
          @apply bg-gray-100 px-3 py-2 border-b border-gray-100;

          &.sticky-col {
            position: sticky;
            z-index: 4;
            background-color: #f3f4f6;
          }

          &.align-middle {
            height: 1px;

            label {
              @apply flex items-center justify-center w-full rounded cursor-pointer;
              height: 100%;
            }

            &:has(input:checked) {
              @apply bg-gray-100;
            }
          }
        }
      }
    }

    tbody {
      tr {
        td {
          @apply px-3 py-2 border-b border-gray-100;
          transition: background-color 0.3s ease;

          &.sticky-col {
            position: sticky;
            z-index: 3;
            background-color: white;

            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0px;
              width: 1px;
              z-index: 20;
              height: 100%;
              background: #f3f4f6;
            }

            &.virtual-box-shadow {
              &::before {
                left: 1px;
              }
            }
          }

          &.align-middle {
            height: 1px;

            label {
              @apply flex items-center justify-center w-full cursor-pointer;
              height: 100%;
            }

            &:hover {
              @apply bg-gray-50;
            }

            &:has(input:checked) {
              @apply bg-[#F0FDF4];
            }

            &:has(input:disabled) {
              @apply bg-gray-50 cursor-not-allowed;

              label {
                @apply cursor-not-allowed;
              }

              input {
                background: #e5e5e5;
              }
            }
          }
        }
      }
    }

    td.sticky-border {
      position: sticky;
      right: 0px;
      z-index: 3;
      width: 1px;
      background-color: #f3f4f6;
      div {
        width: 1px;
        height: 100%;
        background-color: #f3f4f6;
      }

      &.bg-gray-200 {
        background-color: #e5e5e5;
        div {
          background-color: #e5e5e5;
        }
      }
    }
  }
}

/* LTR styles */
.is-ltr .woot-table th.sticky-col-header:first-child {
  left: -1px;
}

.is-ltr .woot-table th.sticky-col-header:nth-child(2) {
  left: 40px;
}

.is-ltr .woot-table td.sticky-col:first-child {
  left: -1px;
}

.is-ltr .woot-table td.sticky-col:nth-child(2) {
  left: 40px;
}

/* RTL styles */
.is-rtl .woot-table th.sticky-col-header:first-child {
  right: -1px;
}

.is-rtl .woot-table th.sticky-col-header:nth-child(2) {
  right: 40px;
}

.is-rtl .woot-table td.sticky-col:first-child {
  right: -1px;
}

.is-rtl .woot-table td.sticky-col:nth-child(2) {
  right: 40px;
}

.is-rtl {
  .inbox-permissions-container {
    &::-webkit-scrollbar-track:horizontal {
      margin-right: 274px;
      margin-left: 0;
    }
  }

  .woot-table {
    thead {
      th {
        &.sticky-col-header {
          border-top-right-radius: 8px;
        }
      }
    }
    tbody {
      tr {
        td {
          &.sticky-col {
            &::before {
              left: unset;
              right: 0;
            }

            &.virtual-box-shadow {
              &::before {
                right: 1px;
              }
            }
          }
        }
      }
    }

    td.sticky-border {
      right: unset;
      left: 0px;
    }
  }
}

.is-ltr {
  .woot-table {
    thead {
      th {
        &.sticky-col-header {
          border-top-left-radius: 8px;
        }
      }
    }
  }
}
</style>
